import { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { type DaySequence, DaySequenceStatus } from '@core/generated/gql-types';
import Button from '@/components/Button';
import { useTenantId } from '@/contexts/TenantContext';
import { usePortalPath } from '@/hooks/usePortalPath';
import type { DateRangeValue } from '@/components/DateRangePicker';
import DaySequenceStatusModal from '@/modules/payroll/components/daySequences/DaySequenceStatusModal';
import PayrollDateRangePicker from '@/modules/payroll/components/shared/PayrollDateRangePicker';
import PayrollStatusPill from '@/modules/payroll/components/shared/PayrollStatusPill';
import { usePayrollDaySequences } from '@/modules/payroll/hooks/usePayrollDaySequences';
import {
  type DaySequenceSortKey,
  type EntitySummary,
  filterDaySequences,
  formatSequenceDuration,
  getDefaultDaySequenceRange,
  groupDaySequencesByJob,
  type SortDirection,
  STATUS_FILTERS,
} from '@/modules/payroll/utils/daySequenceGroups.utils';
import {
  EMPTY_CELL_LABEL,
  formatCents,
} from '@/modules/payroll/utils/payrollFormat.utils';
import type { PayrollView } from '@/modules/payroll/views/payroll.view';

const COLUMNS: { key: DaySequenceSortKey; label: string }[] = [
  { key: 'date', label: 'Date' },
  { key: 'day', label: 'Day' },
  { key: 'duration', label: 'Duration' },
  { key: 'clockIn', label: 'Clock in' },
  { key: 'clockOut', label: 'Clock out' },
  { key: 'break', label: 'Break' },
  { key: 'payRate', label: 'Pay rate' },
  { key: 'chargeRate', label: 'Charge rate' },
  { key: 'status', label: 'Status' },
];

const dateFormatter = new Intl.DateTimeFormat('en-AU', {
  day: '2-digit',
  month: '2-digit',
  year: '2-digit',
});

const linkClassName = 'text-[#03406A] hover:underline';

type PayrollDaySequencesSectionProps = {
  view: PayrollView;
};

/**
 * Day sequences requiring action, grouped by job, with date / status /
 * search filters and inline approve or escalate.
 */
const PayrollDaySequencesSection = ({
  view,
}: PayrollDaySequencesSectionProps) => {
  const tenantId = useTenantId();
  const { path } = usePortalPath();
  const {
    sequences,
    lookups,
    isLoading,
    isLoadingDetails,
    error,
    lastSyncedAt,
    pendingStatusIds,
    refresh,
    updateStatus,
  } = usePayrollDaySequences(tenantId);

  const [searchText, setSearchText] = useState('');
  const [dateRange, setDateRange] = useState<DateRangeValue>(
    getDefaultDaySequenceRange,
  );
  const [activeStatuses, setActiveStatuses] = useState<Set<DaySequenceStatus>>(
    () => new Set(),
  );
  const [sortKey, setSortKey] = useState<DaySequenceSortKey>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [statusPickerSequence, setStatusPickerSequence] =
    useState<DaySequence | null>(null);

  const range = useMemo(
    () => ({ from: dateRange.start.valueOf(), to: dateRange.end.valueOf() }),
    [dateRange],
  );

  const filteredSequences = useMemo(
    () =>
      filterDaySequences(sequences, {
        range,
        statuses: activeStatuses,
        searchText,
        lookups,
      }),
    [sequences, range, activeStatuses, searchText, lookups],
  );

  const groups = useMemo(
    () =>
      groupDaySequencesByJob(
        filteredSequences,
        lookups,
        sortKey,
        sortDirection,
      ),
    [filteredSequences, lookups, sortKey, sortDirection],
  );

  const toggleStatus = (status: DaySequenceStatus) => {
    setActiveStatuses((current) => {
      const next = new Set(current);
      if (next.has(status)) {
        next.delete(status);
      } else {
        next.add(status);
      }
      return next;
    });
  };

  const handleSortChange = (key: DaySequenceSortKey) => {
    if (key === sortKey) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDirection('asc');
  };

  const handleStatusSelect = useCallback(
    async (sequence: DaySequence, status: DaySequenceStatus) => {
      setStatusPickerSequence(null);
      try {
        await updateStatus(sequence, status);
        toast.success('Day sequence status updated');
      } catch (err) {
        toast.error(
          err instanceof Error
            ? err.message
            : 'Unable to update day sequence status.',
        );
      }
    },
    [updateStatus],
  );

  const renderEntityLink = (
    summary: EntitySummary,
    routePrefix: '/workers' | '/clients',
  ) =>
    summary.id && !summary.multiple ? (
      <a
        href={path(`${routePrefix}/${summary.id}`)}
        target="_blank"
        rel="noopener noreferrer"
        className={linkClassName}
      >
        {summary.label}
      </a>
    ) : (
      <span className="text-gray-700">{summary.label}</span>
    );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-4">
        <PayrollDateRangePicker value={dateRange} onChange={setDateRange} />
        <label className="flex min-w-[240px] flex-1 flex-col gap-1 text-sm text-gray-700">
          Search
          <input
            type="search"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm"
            placeholder="Worker, job, or client"
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
          />
        </label>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            className="py-2 text-sm"
            onClick={() => void refresh()}
            isLoading={isLoading}
          >
            Refresh
          </Button>
          {lastSyncedAt && (
            <span className="text-xs text-gray-500">
              Synced {new Date(lastSyncedAt).toLocaleTimeString('en-AU')}
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-gray-700">Statuses:</span>
        <button
          type="button"
          onClick={() => setActiveStatuses(new Set())}
          className={`rounded-full border px-3 py-1 text-xs ${
            activeStatuses.size === 0
              ? 'border-[#03406A] bg-[#03406A] text-white'
              : 'border-gray-300 text-gray-700 hover:bg-gray-50'
          }`}
        >
          All statuses
        </button>
        {STATUS_FILTERS.map((status) => (
          <button
            key={status.key}
            type="button"
            onClick={() => toggleStatus(status.key)}
            className={`rounded-full border px-3 py-1 text-xs ${
              activeStatuses.has(status.key)
                ? 'border-[#03406A] bg-[#03406A] text-white'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            {status.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}
      {isLoadingDetails && (
        <p className="text-xs text-gray-500">
          Loading worker, job, and client info…
        </p>
      )}

      {isLoading && sequences.length === 0 ? (
        <p className="py-6 text-sm text-gray-500">Loading day sequences…</p>
      ) : groups.length === 0 ? (
        <p className="py-6 text-sm text-gray-500">
          No day sequences match the current filters.
        </p>
      ) : (
        <div className="max-h-[65vh] overflow-auto rounded-md border border-gray-200">
          <table className="min-w-full border-collapse text-left text-sm">
            <thead className="sticky top-0 z-10 bg-gray-50">
              <tr>
                {COLUMNS.map((column) => (
                  <th
                    key={column.key}
                    className="border-b border-gray-200 px-3 py-2 font-medium whitespace-nowrap text-gray-700"
                  >
                    <button
                      type="button"
                      className="hover:text-[#03406A]"
                      onClick={() => handleSortChange(column.key)}
                    >
                      {column.label}
                      {sortKey === column.key
                        ? sortDirection === 'asc'
                          ? ' ▲'
                          : ' ▼'
                        : ''}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            {groups.map((group) => (
              <tbody key={group.key}>
                <tr className="bg-[#03406A]/5">
                  <td colSpan={COLUMNS.length} className="px-3 py-2">
                    <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
                      {group.jobId ? (
                        <a
                          href={path(`/jobs/${group.jobId}`)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-semibold text-[#03406A] hover:underline"
                        >
                          {group.label}
                        </a>
                      ) : (
                        <span className="font-semibold text-gray-900">
                          {group.label}
                        </span>
                      )}
                      {group.durationText && (
                        <span className="text-xs text-gray-500">
                          {group.durationText}
                        </span>
                      )}
                      <span className="text-xs text-gray-500">
                        Worker:{' '}
                        {renderEntityLink(group.workerSummary, '/workers')}
                      </span>
                      <span className="text-xs text-gray-500">
                        Client:{' '}
                        {renderEntityLink(group.clientSummary, '/clients')}
                      </span>
                    </div>
                  </td>
                </tr>
                {group.rows.map((row) => {
                  const isUpdating = pendingStatusIds.has(row.sequence.id);
                  const canChangeStatus =
                    view.actions.daySequenceStatusOptions.length > 0 &&
                    row.sequence.status !== DaySequenceStatus.PayProcessed;
                  return (
                    <tr
                      key={row.sequence.id}
                      className="border-b border-gray-100 hover:bg-gray-50"
                    >
                      <td className="px-3 py-2 whitespace-nowrap">
                        {row.timestamp !== null
                          ? dateFormatter.format(row.timestamp)
                          : EMPTY_CELL_LABEL}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                        {row.totalDays > 1
                          ? `Day ${row.dayPosition} of ${row.totalDays}`
                          : `Day ${row.dayPosition}`}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {formatSequenceDuration(row.sequence)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {row.clockInLabel}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {row.clockOutLabel}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {row.breakLabel}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {formatCents(row.payRateCents)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {formatCents(row.chargeRateCents)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {isUpdating ? (
                          <PayrollStatusPill label="Updating…" />
                        ) : canChangeStatus ? (
                          <button
                            type="button"
                            title="Change status"
                            onClick={() =>
                              setStatusPickerSequence(row.sequence)
                            }
                            className="rounded-full hover:ring-2 hover:ring-[#03406A]/30"
                          >
                            <PayrollStatusPill status={row.sequence.status} />
                          </button>
                        ) : (
                          <PayrollStatusPill status={row.sequence.status} />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            ))}
          </table>
        </div>
      )}

      <p className="text-xs text-gray-500">
        Showing {filteredSequences.length}{' '}
        {filteredSequences.length === 1 ? 'day sequence' : 'day sequences'}.
      </p>

      <DaySequenceStatusModal
        sequence={statusPickerSequence}
        options={view.actions.daySequenceStatusOptions}
        onSelect={(sequence, status) =>
          void handleStatusSelect(sequence, status)
        }
        onClose={() => setStatusPickerSequence(null)}
      />
    </div>
  );
};

export default PayrollDaySequencesSection;
